<template>
    <p-modal
        name="rentalReconciliationModal"
        label="Rental Reconciliation"
        ref="rentalReconciliation"
        size="lg"
        @ok.prevent="handleModalOk"
    >
        <template v-slot:default>
            <p-form ref="rentalReconciliationForm">
                <div class="container-fluid content-wrapper">
                    <b-row>
                        <b-col>
                            <p>Rental Reconciliation by Term</p>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <p-card>
                            <b-row cols="1" cols-lg="2">
                            <b-col>
                                <p-select
                                    label="Rental Source"
                                    :options="sourceOptions"
                                    v-model="reportParameters.rentalSource"
                                   :disabled='true'
                                />
                            </b-col>
                            <b-col>
                                <p-advanced-select
                                    shortDropdown
                                    dataType="rentalTerms"
                                    name="Term"
                                    label="Rental Term"
                                    v-model="reportParameters.termId"
                                    rules="required"
                                />
                            </b-col>
                            </b-row>
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate 
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
        <template  v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button
                        variant="outline-primary"
                        :is-busy="isBusy"
                        @click="cancel"
                        >Cancel</p-button
                    >
                    <p-button
                        variant="primary"
                        @click="ok"
                        :is-busy="isBusy"
                        class="ms-2"
                        >Export CSV</p-button
                    >
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import pCard from '@/components/p-card.vue';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    mixins: [ReportTemplates],
    components: { pCard, UserReportTemplate },
    data() {
        return {
            isBusy: false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Rental Reconciliation',
            disabled: true,
            reportParameters: {
                registerIds: [],
                rentalSource: 'All Sources',
                termId: null
            },
            sourceOptions: [
                { value: 'All Sources', text: 'All Sources' },
                { value: 'Store Managed', text: 'Store Managed' },
                { value: 'None', text: 'None' }
            ]
        };
    },
    computed: {
        pageTitle() {
            return 'Rental Reconciliation';
        }
    },
    methods: {
        async show() {
            this.reportParameters.registerIds = [];
            this.reportParameters.termId = 0;
            this.reportParameters.rentalSource = 'Store Managed';

            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            await this.loadUserReportTemplates();
            this.$refs.rentalReconciliation.show();
        },
        // ReportTemplates hook: return false when anything in the saved template can no longer be
        // selected, and the mixin warns and loads nothing. The rental terms list only includes
        // terms that have rental periods, so a saved term can drop out of it, and
        // p-advanced-select won't always catch that on its own — it only re-checks its bound value
        // when its option list contains disabled entries.
        async validateReportParameters(savedParameters) {
            if (!this.sourceOptions.some(sourceOption => sourceOption.value === savedParameters.rentalSource)) {
                return false;
            }

            const rentalTerms = await selectListOptionsDataContext.getStoreDropdownData('rentalTerms');

            return rentalTerms.some(rentalTerm => rentalTerm.value == savedParameters.termId);
        },
        handleModalOk: async function() {
            if (!(await this.$refs.rentalReconciliationForm.validate())) {
                return;
            }

            if (this.reportParameters.termId === 0) {
                this.$appError(
                    'Please select a term.'
                );
                return;
            }

            this.isBusy = true;

            // The current location always wins: a restored template carries the location that was
            // active when it was saved, which would otherwise run the report for the wrong one.
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            if (!(await this.saveUserReportTemplate())) {
                this.isBusy = false;
                return;
            }

            await axios
                .post(
                    '/sales/exportRentalReconciliationCsv',
                    this.reportParameters,
                    {
                        responseType: 'blob'
                    }
                )
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        'Rental Reconciliation.csv',
                        'application/csv'
                    );
                    this.$appSuccess(
                            `Rental reconciliation report generated successfully.`
                        )
                    this.$refs.rentalReconciliation.hide();
                })
                .finally(() => {
                    this.isBusy = false;
                });
        }
    }
};
</script>

<style scoped>
.static-height {
    height:370px
}
</style>
